/**
 * Matomo tracking utilities
 * Initializes Matomo tracking and provides functions for tracking page views and events
 */

const SEO_ENTRY_KEY = 'av_seo_entry_page';
const IS_DEV = process.env.NODE_ENV !== 'production';

const NON_SEO_PATH_PREFIXES = [
  '/login',
  '/register',
  '/dashboard',
  '/admin',
  '/magic-link',
  '/verify-email',
  '/join',
  '/group',
  '/resolve-token',
  '/404',
];

function isTrackableSeoPath(pathname) {
  if (!pathname || pathname === '/') {
    return true;
  }

  const normalized = pathname.replace(/\/+$/, '') || '/';

  return !NON_SEO_PATH_PREFIXES.some((prefix) => normalized === prefix || normalized.endsWith(prefix));
}

/**
 * Initialize Matomo tracking
 * This function loads the Matomo script if the environment variables are configured
 */
export const initializeMatomo = () => {
  const matomoUrl = process.env.REACT_APP_MATOMO_URL;
  const siteId = process.env.REACT_APP_MATOMO_SITE_ID;

  if (!matomoUrl || !siteId) {
    if (IS_DEV) {
      console.log('Matomo tracking disabled: missing configuration');
    }
    return;
  }

  if (window._paq && document.querySelector(`script[src*="${matomoUrl}"]`)) {
    return;
  }

  window._paq = window._paq || [];

  window._paq.push(['setTrackerUrl', `${matomoUrl}/matomo.php`]);
  window._paq.push(['setSiteId', siteId]);

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `${matomoUrl}/matomo.js`;
  script.onerror = () => {
    if (IS_DEV) {
      console.error('Failed to load Matomo tracking script');
    }
  };

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
};

/**
 * Track a page view
 * @param {string} customPath - Optional custom path to track (defaults to current path)
 */
export const trackPageView = (customPath = null) => {
  if (!window._paq) {
    return;
  }

  if (customPath) {
    window._paq.push(['setCustomUrl', customPath]);
  }
  window._paq.push(['trackPageView']);
};

/**
 * Track an event
 * @param {string} category - Event category
 * @param {string} action - Event action
 * @param {string} name - Event name (optional)
 * @param {number} value - Event value (optional)
 */
export const trackEvent = (category, action, name = null, value = null) => {
  if (!window._paq) {
    return;
  }

  const event = ['trackEvent', category, action];
  if (name) {
    event.push(name);
    if (value !== null) {
      event.push(value);
    }
  }
  window._paq.push(event);
};

/**
 * Track a CTA click toward registration.
 * @param {string} sourcePath - Page path where the CTA was clicked
 */
export const trackConversionEvent = (sourcePath = null) => {
  const path = sourcePath || window.location.pathname;
  trackEvent('conversion', 'cta_register', path);
};

/**
 * Track a click on the demo booking CTA.
 * @param {string} sourcePath - Page path where the CTA was clicked
 */
export const trackDemoEvent = (sourcePath = null) => {
  const path = sourcePath || window.location.pathname;
  trackEvent('conversion', 'cta_demo', path);
};

/** External URL for « Réserver une démo » (mes-rdv or custom). */
export const getDemoBookingUrl = () =>
  process.env.REACT_APP_DEMO_BOOKING_URL || 'https://mes-rdv.fr/';

/**
 * Remember the first SEO landing page in the session for attribution.
 * @param {string} pathname
 */
export const rememberSeoEntryPage = (pathname) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  if (sessionStorage.getItem(SEO_ENTRY_KEY)) {
    return;
  }

  if (!isTrackableSeoPath(pathname)) {
    return;
  }

  sessionStorage.setItem(SEO_ENTRY_KEY, pathname);
};

/**
 * @returns {string}
 */
export const getSeoEntryPage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return '(direct)';
  }

  return sessionStorage.getItem(SEO_ENTRY_KEY) || '(direct)';
};

/**
 * Track a successful registration with SEO entry attribution.
 */
export const trackRegistrationSuccess = () => {
  const entryPage = getSeoEntryPage();
  setCustomVariable(1, 'seo_entry_page', entryPage, 'visit');
  trackEvent('conversion', 'register_success', entryPage);
};

/**
 * Track a Core Web Vital metric in Matomo.
 * @param {string} metricName - LCP, CLS, FID, INP, etc.
 * @param {number} value
 * @param {string} pathname
 */
export const trackWebVital = (metricName, value, pathname = null) => {
  const path = pathname || window.location.pathname;
  trackEvent('web_vitals', metricName, path, Math.round(value));
};

/**
 * Set custom variable
 * @param {number} index - Variable index (1-5)
 * @param {string} name - Variable name
 * @param {string} value - Variable value
 * @param {string} scope - Variable scope ('visit' or 'page')
 */
export const setCustomVariable = (index, name, value, scope = 'visit') => {
  if (!window._paq) {
    return;
  }

  window._paq.push(['setCustomVariable', index, name, value, scope]);
};

/**
 * Set user ID for tracking
 * @param {string} userId - User ID to track
 */
export const setUserId = (userId) => {
  if (!window._paq) {
    return;
  }

  window._paq.push(['setUserId', userId]);
};

/**
 * Reset user ID
 */
export const resetUserId = () => {
  if (!window._paq) {
    return;
  }

  window._paq.push(['resetUserId']);
};
